import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const COMMON_USAGES = [
  {
    module: 'eraf-data-jpa',
    api: 'ConfigService.list/get/setConfig/delete (C085)',
    role: '★ 설정 CRUD 핵심 위임',
    snippet: 'configService.setConfig(req)',
  },
  {
    module: 'eraf-data-jpa',
    api: 'ConfigChangedEvent (C085)',
    role: '★ 동적 갱신 — 변경 시 발행 → 구독자 즉시 반영',
    snippet: '@TransactionalEventListener void on(ConfigChangedEvent e)',
  },
  {
    module: 'eraf-data-jpa',
    api: 'ConfigEntryRequest/Response (C085)',
    role: '요청/응답 DTO + 민감 **** 마스킹',
    snippet: 'ConfigEntryResponse.from(entity)',
  },
  {
    module: 'eraf-core',
    api: 'ConfigEncryptor.isEnvelope (C016)',
    role: 'yml ENC(...) 봉투 암호화 메타 안내',
    snippet: 'encryptor.isEnvelope(value)',
  },
  {
    module: 'eraf-data-jpa',
    api: '@SensitiveColumn / SensitiveStringConverter (C044)',
    role: '민감 config_value 봉투 암복호화 (자동)',
    snippet: '(C085 내부 — biz 코드 0줄)',
  },
  {
    module: 'eraf-data-jpa',
    api: '@Cacheable/@CacheEvict("configEntries") (C051)',
    role: '조회 캐시 적중 + 쓰기 evict (자동)',
    snippet: '(C085 내부 — biz 코드 0줄)',
  },
  {
    module: 'eraf-data-jpa',
    api: 'FeatureToggleService / @FeatureToggle (C020)',
    role: '민감값 평문 노출 토글 게이트 (기본 OFF)',
    snippet: '@FeatureToggle("config-center.sensitive-reveal")',
  },
  {
    module: 'eraf-core',
    api: 'ApiResponse.page(...) (C002)',
    role: '목록 표준 응답',
    snippet: 'return ApiResponse.page(service.list(cat, p))',
  },
  {
    module: 'eraf-core',
    api: '@Audited(action="CONFIG_CHANGED") (C014)',
    role: '변경 자기 감사 → audit-log 소비',
    snippet: '@Audited(action="CONFIG_CHANGED", resource="config-center")',
  },
  {
    module: 'eraf-security',
    api: '@HasPermission("CONFIG_CENTER_WRITE") (C030)',
    role: '메서드 권한 (이중 방어)',
    snippet: '@HasPermission("CONFIG_CENTER_WRITE")',
  },
  {
    module: 'eraf-security',
    api: 'SecurityChainCustomizer + PathPolicy (C029)',
    role: '/config-center/** 권한 정책',
    snippet: 'PathPolicy.hasPermission(...)',
  },
];

/**
 * ConfigCommonUsagePanel — 이 페이지가 사용한 공통 모듈 (biz-foundation §4.11 의무 패널)
 * 접을 수 있는 accordion
 */
export function ConfigCommonUsagePanel() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <div
      style={{
        marginTop: 'var(--spacing-6)',
        border: '1px solid var(--color-surface-border)',
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
      }}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls="config-common-usage-body"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: 'var(--spacing-3) var(--spacing-4)',
          background: 'var(--color-surface-muted)',
          border: 'none',
          borderBottom: open ? '1px solid var(--color-surface-border)' : 'none',
          cursor: 'pointer',
          gap: 'var(--spacing-2)',
          textAlign: 'left',
          fontSize: 'var(--font-size-sm)',
          fontWeight: 600,
          color: 'var(--color-text-secondary)',
          fontFamily: 'var(--font-family-sans)',
        }}
      >
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
        {t('configCenter.commonUsage.title')}
        <span
          style={{
            marginLeft: 'auto',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--color-text-muted)',
            fontWeight: 400,
          }}
        >
          {t('configCenter.commonUsage.description')}
        </span>
      </button>

      {open && (
        <div id="config-common-usage-body" style={{ overflowX: 'auto' }}>
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            <thead>
              <tr style={{ background: 'var(--color-surface-muted)' }}>
                {(['module', 'api', 'role', 'snippet'] as const).map((col) => (
                  <th
                    key={col}
                    style={{
                      textAlign: 'left',
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t(`configCenter.commonUsage.col.${col}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {COMMON_USAGES.map((row, i) => (
                <tr key={i}>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {row.module}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-brand-primary)',
                      fontFamily: 'var(--font-family-mono)',
                    }}
                  >
                    {row.api}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-primary)',
                    }}
                  >
                    {row.role}
                  </td>
                  <td
                    style={{
                      padding: 'var(--spacing-2) var(--spacing-3)',
                      borderBottom: '1px solid var(--color-surface-border)',
                      color: 'var(--color-text-muted)',
                      fontFamily: 'var(--font-family-mono)',
                    }}
                  >
                    {row.snippet}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
